import { languageDisplayLabel } from '@/api/enums/languages.enum';
import { DialogContext } from '@/common/context';
import { DialogType } from '@/system-settings/enums';
import { FlexContainer } from '@/ui/styled-ui';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { EditorSidebarPanel } from './EditorSidebar';

const StyledDropdown = styled(Dropdown)`
  .p-dropdown-label.p-inputtext {
    padding-right: 0px;
    unicode-bidi: normal;
    direction: inherit;
  }
`;

const StyledMultiSelect = styled(MultiSelect)`
  background: var(--white-main);
  border-color: var(--purple-dark);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  border-radius: var(--default-border-radius);
  gap: var(--xsmall-padding) !important;

  .p-placeholder,
  .p-multiselect-label {
    color: var(--black-main) !important;
    padding-right: 0 !important;
  }

  .p-multiselect-label {
    white-space: nowrap;
  }

  &.p-focus {
    border-color: var(--purple-dark) !important;
    box-shadow: none !important;
  }

  &:hover {
    background: var(--red-dark);
    border-color: var(--red-dark);

    .p-placeholder,
    .p-multiselect-label,
    svg {
      color: var(--white-main) !important;
    }
  }
`;

type LanguageSettingsPanelProps<TLanguage extends string> = {
  selectedLanguage: TLanguage;
  languages: TLanguage[];
  onLanguageChange: (language: TLanguage) => void;
  onCopyToLanguage: (languages: TLanguage[]) => void;
};

export const LanguageSettingsPanel = <TLanguage extends string>({
  selectedLanguage,
  languages,
  onLanguageChange,
  onCopyToLanguage,
}: LanguageSettingsPanelProps<TLanguage>) => {
  const { t } = useTranslation();
  const { setDialogData } = useContext(DialogContext);

  const [targetLanguagesForCopy, setTargetLanguagesForCopy] = useState<
    TLanguage[]
  >([]);
  const [showCopyLanguages, setShowCopyLanguages] = useState(false);

  const languageOptions = useMemo(
    () =>
      languages?.map((language) => ({
        label: languageDisplayLabel[language] ?? language,
        value: language,
      })) ?? [],
    [languages],
  );

  const targetLanguageOptions = useMemo(() => {
    const source = showCopyLanguages ? targetLanguagesForCopy : languages;

    return (
      source
        ?.filter((language) => language !== selectedLanguage)
        ?.map((language) => ({
          label: languageDisplayLabel[language] ?? language,
          value: language,
        })) ?? []
    );
  }, [showCopyLanguages, targetLanguagesForCopy, languages, selectedLanguage]);

  const dropdownFooter = () => (
    <div>
      <button
        className="underline cursor-pointer show-selected-text"
        onClick={() => setShowCopyLanguages(!showCopyLanguages)}
      >
        {showCopyLanguages ? t('generic.showAll') : t('generic.showSelected')}
      </button>
    </div>
  );

  const getConfirmMessage = (targets: TLanguage[]) => {
    const labels = targets
      .map((language) => languageDisplayLabel[language] ?? language)
      .join(', ');

    if (targets.length === 1) {
      return {
        warning: t('content.slides.copyToLanguage.confirm', {
          language: labels,
        }),
        note: t('content.slides.copyToLanguage.confirm.note'),
      };
    }

    return {
      warning: t('content.slides.copyToLanguages.confirm', {
        languages: labels,
      }),
      note: t('content.slides.copyToLanguages.confirm.note'),
    };
  };

  const handleMultiSelectHide = () => {
    if (targetLanguagesForCopy.length > 0) {
      const { warning, note } = getConfirmMessage(targetLanguagesForCopy);

      setDialogData({
        type: DialogType.CONFIRMATION,
        show: true,
        closeImmediatelyAfterAccept: true,
        header: t('dialog.confirm'),
        customContent: (
          <p className="m-0">
            {warning}
            <br />
            {note}
          </p>
        ),
        onAccept: () => {
          onCopyToLanguage(targetLanguagesForCopy);
        },
      });
    }

    setTargetLanguagesForCopy([]);
  };

  return (
    <EditorSidebarPanel header={t('content.language.settings')}>
      <FlexContainer gap={8}>
        <StyledDropdown
          value={selectedLanguage}
          options={languageOptions}
          onChange={(e) => onLanguageChange(e.value)}
          className="w-full"
        />
        <StyledMultiSelect
          value={targetLanguagesForCopy}
          options={targetLanguageOptions}
          onChange={(e) => setTargetLanguagesForCopy(e.value)}
          placeholder={t('generic.copyTo')}
          selectAllLabel={t('content.select.all.languages')}
          maxSelectedLabels={0}
          selectedItemsLabel={t('generic.copyTo')}
          panelFooterTemplate={dropdownFooter}
          onHide={handleMultiSelectHide}
        />
      </FlexContainer>
    </EditorSidebarPanel>
  );
};
